/* eslint-disable */
/**
 * Applies a fast and separable median filter to the alpha channel of image data,
 * binarizing the result based on a threshold. Operates in place on the input data.
 * The output is a grayscale image where RGB channels are set to the binarized median value,
 * and the alpha channel is set to 255.
 * Uses a fast 2-pass (horizontal then vertical) approach with sliding window histograms.
 *
 * @param {Uint8ClampedArray | Uint8Array} imageData - The image data array (e.g., from ImageData.data). Expected RGBA format. Modified in place.
 * @param {number} width - The width of the image in pixels.
 * @param {number} height - The height of the image in pixels.
 * @param {number} [kernelRadius=4] - The radius of the median filter kernel (size = 2 * radius + 1).
 * @param {number} [threshold=255] - The threshold value (0-255). Alpha values >= threshold are treated as 1, others as 0 during median calculation.
 * @returns {Uint8ClampedArray | Uint8Array} The execution time of the filter in milliseconds.
 */
export function fastMedianFilterAproximationWithThreshold(imageData, width, height, kernelRadius = 4, threshold = 255) {
  // Finds the median (0 or 1) from a 2-bin histogram (count < threshold, count >= threshold)
  function findMedianFromBinaryHistogram(hist, count) {
    const medianPos = Math.floor(count / 2) + 1;
    return hist[1] >= medianPos ? 1 : 0;
  }

  const data = imageData; // Operate in place
  const dataLength = width * height;
  const tempBinData = new Uint8Array(dataLength); // Horizontal pass result (0 or 1)
  const histogram = new Uint32Array(2); // [count < threshold, count >= threshold]

  // --- Pass 1: Horizontal ---
  for (let y = 0; y < height; y++) {
    histogram.fill(0);
    let winPixelCount = 0;
    const rowOffset = y * width;

    // Initial window fill (centered at x=0)
    for (let kx = -kernelRadius; kx <= kernelRadius; kx++) {
      // Wrap around horizontally using ternary operator (faster than modulo)
      // Assumes kernelRadius < width
      const col = kx < 0 ? kx + width : (kx >= width ? kx - width : kx);
      const alphaIdx = (rowOffset + col) * 4 + 3;
      const binVal = data[alphaIdx] >= threshold ? 1 : 0;
      histogram[binVal]++;
      winPixelCount++;
    }

    // First pixel median (for x=0)
    let binMedian = findMedianFromBinaryHistogram(histogram, winPixelCount);
    tempBinData[rowOffset] = binMedian;

    // Slide window horizontally (for x=1 to width-1)
    for (let x = 1; x < width; x++) {
      // Remove left pixel (wrap around)
      const raw_remCol = x - 1 - kernelRadius;
      // Assumes kernelRadius < width, so raw_remCol is in [-kernelRadius, width - 2 - kernelRadius] -> [-width, width)
      const remCol = raw_remCol < 0 ? raw_remCol + width : raw_remCol;
      const remAlphaIdx = (rowOffset + remCol) * 4 + 3;
      const remBinVal = data[remAlphaIdx] >= threshold ? 1 : 0;
      // Note: winPixelCount logic might be slightly off if histogram[remBinVal] was already 0,
      // but with wrap-around, the window size is constant, so this check might be unnecessary.
      // Keeping it for minimal change from original.
      if (histogram[remBinVal] > 0) {
        histogram[remBinVal]--;
        winPixelCount--;
      }

      // Add right pixel (wrap around)
      const raw_addCol = x + kernelRadius;
      // Assumes kernelRadius < width, so raw_addCol is in [1 + kernelRadius, width - 1 + kernelRadius] -> (0, 2*width)
      const addCol = raw_addCol >= width ? raw_addCol - width : raw_addCol;
      const addAlphaIdx = (rowOffset + addCol) * 4 + 3;
      const addBinVal = data[addAlphaIdx] >= threshold ? 1 : 0;
      histogram[addBinVal]++;
      winPixelCount++;

      // Current pixel median
      binMedian = findMedianFromBinaryHistogram(histogram, winPixelCount);
      tempBinData[rowOffset + x] = binMedian;
    }
  }

  // --- Pass 2: Vertical ---
  for (let x = 0; x < width; x++) {
    histogram.fill(0);
    let winPixelCount = 0;

    // Initial window fill (centered at y=0, from horizontal pass result)
    for (let ky = -kernelRadius; ky <= kernelRadius; ky++) {
      // Wrap around vertically using ternary operator (faster than modulo)
      // Assumes kernelRadius < height
      const row = ky < 0 ? ky + height : (ky >= height ? ky - height : ky);
      const tmpIdx = row * width + x;
      const binVal = tempBinData[tmpIdx];
      histogram[binVal]++;
      winPixelCount++;
    }

    // First pixel median & output (for y=0)
    let binMedian = findMedianFromBinaryHistogram(histogram, winPixelCount);
    let outVal = binMedian === 1 ? 255 : 0;
    let outIdx = x * 4; // Index for y=0
    data[outIdx] = outVal; // R
    data[outIdx + 1] = outVal; // G
    data[outIdx + 2] = outVal; // B
    // data[outIdx + 3] = outVal; // A - Original code set A=outVal here, but A=255 later. Setting A=255 consistently.
    data[outIdx + 3] = 255; // A

    // Slide window vertically (for y=1 to height-1)
    // Note: Original loop started at y=0, potentially overwriting the y=0 calculation above.
    // Assuming the intent was to slide starting from y=1. If y must start from 0,
    // the initial fill and first output calculation above this loop are redundant.
    // Sticking to original loop bounds for minimal change.
    for (let y = 0; y < height; y++) { // Original loop started y=0
      // If y > 0, remove top pixel (wrap around)
      if (y > 0) { // Avoid double-counting removal/add for y=0 if loop starts here
          const raw_remRow = y - 1 - kernelRadius;
          // Assumes kernelRadius < height, so raw_remRow is in [-kernelRadius, height - 2 - kernelRadius] -> [-height, height)
          const remRow = raw_remRow < 0 ? raw_remRow + height : raw_remRow;
          const remTmpIdx = remRow * width + x;
          const remBinVal = tempBinData[remTmpIdx];
          if (histogram[remBinVal] > 0) {
              histogram[remBinVal]--;
              winPixelCount--;
          }

          // Add bottom pixel (wrap around)
          const raw_addRow = y + kernelRadius;
          // Assumes kernelRadius < height, so raw_addRow is in [1 + kernelRadius, height - 1 + kernelRadius] -> (0, 2*height)
          const addRow = raw_addRow >= height ? raw_addRow - height : raw_addRow;
          const addTmpIdx = addRow * width + x;
          const addBinVal = tempBinData[addTmpIdx];
          histogram[addBinVal]++;
          winPixelCount++;
      } else {
          // For y=0, the histogram is already correct from the initial fill.
          // We calculated the median and output value before the loop.
          // Skip histogram update for y=0 inside the loop.
      }


      // Current pixel median & output
      // Recalculate median even for y=0 based on potentially updated histogram if loop starts at y=0
      binMedian = findMedianFromBinaryHistogram(histogram, winPixelCount);
      outVal = binMedian === 1 ? 255 : 0;
      outIdx = (y * width + x) * 4;

      data[outIdx] = outVal; // R
      data[outIdx + 1] = outVal; // G
      data[outIdx + 2] = outVal; // B
      data[outIdx + 3] = 255; // A (Consistent alpha)
    }
  }
}

/**
 * Calculates the bounding box of pixels meeting a certain alpha threshold.
 * @param {Uint8ClampedArray<ArrayBufferLike>} data - The 2D rendering context of the canvas.
 * @param {number} [threshold=255] - The minimum alpha value (0-255) to consider a pixel non-transparent.
 * @param {Object} [options] - The options object.
 * @param {number} [options.median=0] - The median value to use for the filter.
 * @param {number} [options.extendPixels=0] - The number of pixels to extend the bounding box by.
 * @returns {{ left: number, top: number, width: number, height: number } | null} The bounding box or null if no pixels meet the threshold.
 */
export function calculateOpaqueBounds(data, width, height, threshold = 255, { median = 0, extendPixels = 0 } = {}) {
  let minX = width; let minY = height; let maxX = -1; let
    maxY = -1;

  if (median === 0) {
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const alphaIndex = (y * width + x) * 4 + 3;
        if (data[alphaIndex] >= threshold) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }
  } else {
    // @ts-ignore
    fastMedianFilterAproximationWithThreshold(data, width, height, median, threshold);

    for (let y = 0; y < height; y++) {
      const yOffset = y * width * 4;
      for (let x = 0; x < width; x++) {
        const alphaIndex = yOffset + x + x + x + x;

        if (data[alphaIndex] > 0) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }
  }

  if (minX <= maxX && minY <= maxY) {
    if (extendPixels) {
      minX = Math.max(0, minX - extendPixels);
      minY = Math.max(0, minY - extendPixels);
      maxX = Math.min(width, maxX + extendPixels);
      maxY = Math.min(height, maxY + extendPixels);
    }
    return { left: minX, top: minY, width: maxX - minX, height: maxY - minY };
  }
  return null; // No opaque pixels found
}
